import Vue from 'vue'

const en = require("./lang/eng")
const th = require("./lang/thai")
const ms = require("./lang/malay")
const zhHK = require("./lang/chinese-hk")

type StoredLocale = 'EN' | 'TH' | 'MS' | 'ZH_HK' | 'FALSE'

const dictionaries: Record<StoredLocale, any> = {
  EN: en.eng,
  TH: th.thai,
  MS: ms.malay,
  ZH_HK: zhHK.chinese_hk,
  FALSE: false,
}

const uiToStored: Record<string, StoredLocale> = {
  en: 'EN',
  th: 'TH',
  ms: 'MS',
  'zh-HK': 'ZH_HK',
}

// Reverse map: the stored locale we persist → the UI code we put on
// <body data-language="…"> so CSS font rules (Kanit for Thai, Noto Sans TC
// for zh-HK, etc.) can key off the live language.
const storedToUi: Record<StoredLocale, string> = {
  EN: 'en',
  TH: 'th',
  MS: 'ms',
  ZH_HK: 'zh-HK',
  FALSE: 'en',
}

function readStoredLocale(): StoredLocale {
  if (typeof localStorage === 'undefined') return 'EN'

  const saved = localStorage.getItem('lang')
  if (saved === 'EN' || saved === 'TH' || saved === 'MS' || saved === 'ZH_HK' || saved === 'FALSE') {
    return saved
  }
  return 'EN'
}

function applyBodyLanguageAttr(stored: StoredLocale) {
  if (typeof document === 'undefined') return
  document.body.setAttribute('data-language', storedToUi[stored])
}

const state = Vue.observable({
  current: readStoredLocale(),
})

// Apply the attribute immediately so the first render already uses the
// right font, regardless of whether header.vue has mounted yet.
if (typeof document !== 'undefined') {
  if (document.body) {
    applyBodyLanguageAttr(state.current)
  } else {
    document.addEventListener('DOMContentLoaded', () => applyBodyLanguageAttr(state.current))
  }
}

export function setLocale(code: string) {
  const stored = uiToStored[code]
  if (!stored || state.current === stored) return
  state.current = stored
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('lang', stored)
  }
  applyBodyLanguageAttr(stored)
}

// Translate a dot-path key. Same lookup as `$t()` in templates — usable
// outside Vue components (router guards, store actions, non-component modules).
// `params` fills vue-i18n-style named placeholders: t('a.b', { count: 3 })
// replaces every `{count}` in the string. Unknown placeholders are left
// as-is so a missing param is visible rather than silently blanked.
export function t(path: string, params?: Record<string, string | number>): string {
  let current: any = dictionaries[state.current]
  path = path.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '')
  const parts = path.split('.')
  for (const k of parts) {
    if (!current) return '▒▓▒▓▒▓▒'
    if (k in current) current = current[k]
    else return ' %_' + k + '_% '
  }
  if (params && typeof current === 'string') {
    return current.replace(/\{(\w+)\}/g, (match: string, name: string) =>
      (name in params ? String(params[name]) : match))
  }
  return current
}

const language = {
  install(Vue: any) {
    Vue.mixin({
      methods: {
        $t(path: string, params?: Record<string, string | number>) {
          return t(path, params)
        },
      },
    })
  },
}

Vue.use(language)
